import Api from "./api";
import Service from "./base";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import Notifications from "../utils/notifications";
import { Dialog } from "../ui/framework/dialog";
import TranslationService from "./translation";
import { htmlEscape } from "../utils/vanilla";
import { DriveStore } from "../store/reducers";
import { SecurityService } from "../utils/security";

interface INewsNotificationDataInfo {
    textInfo: string;
    textFullInfo: string;
    htmlInfo: string;
}

interface INewsNotificationData extends IIdEntity {
    info: INewsNotificationDataInfo;
}

interface INewsNotification extends IIdEntity {
    dateTime: string;
    dateTimeEnd: string;
    notificationNewsData: INewsNotificationData;
    recipient: IRecipient;
    version: string;
}

export default class News extends Service {
    private readonly api: Api;
    private readonly translationService: TranslationService;
    private currentCall: Promise | undefined;
    private startAfterVersion: string | undefined;

    constructor(
        private readonly store: DriveStore,
        api: Api,
        translationService: TranslationService,
        private readonly securityService: SecurityService
    ) {
        super();
        this.api = api;
        this.translationService = translationService;
    }

    check() {
        let userId = this.api.getUser()?.id;
        if (!userId || !this.securityService.can("getNews", userId)) {
            return Promise.resolve([]);
        }

        this.currentCall = this.get(this.startAfterVersion)
            .then(notifications => {
                this.startAfterVersion = notifications && notifications.length > 0 ? notifications[notifications.length - 1].version : undefined;
                let newsNotifications: INewsNotification[] = notifications.filter((n: INewsNotification) => {
                    let htmlInfo = n.notificationNewsData?.info?.htmlInfo;
                    return !htmlInfo || (htmlInfo && htmlInfo.indexOf("geotab.checkmate.") === -1);
                });

                newsNotifications.forEach(notification => {
                    let info = notification.notificationNewsData?.info;
                    let newsNotificationId = `news-${ notification.id }`;

                    info && Notifications.add(newsNotificationId, {
                        message: info.textInfo,
                        type: "news",
                        isTranslated: true,
                        action: () => {
                            return new Dialog(
                                newsNotificationId,
                                `<div class="noTranslate">${ info.htmlInfo || htmlEscape(info.textFullInfo) }</div>`,
                                this.translationService.getTranslator(),
                                `<span class="noTranslate">${ info.textInfo }</div>`,
                                [
                                    {
                                        id: `${ newsNotificationId }-dismissButton`,
                                        text: "Dismiss",
                                        callback: dialog => {
                                            dialog.remove();
                                            return this.dismiss(notification);
                                        }
                                    }
                                ],
                                {
                                    isNightMode: this.store.getState().data.nightMode
                                }
                            ).open();
                        }
                    }, userId as string);
                });
                return newsNotifications;
            });
        return this.currentCall;
    }

    destroy() {
        this.startAfterVersion = undefined;
        if (this.currentCall) {
            this.currentCall.cancel();
        }
    }

    private dismiss(newsNotification: INewsNotification) {
        let userId = this.api.getUser()?.id;
        if (userId && this.securityService.can("dismissNews", userId)) {
            return this.api.callPromise("DismissNotification", {
                notification: {
                    id: newsNotification.id
                }
            });
        } else {
            return Promise.resolve();
        }
    }

    private get(startAfterVersion?: string): Promise<INewsNotification[]> {
        return this.api.callPromise("GetUserNotifications", {
            startAfterVersion
        });
    }
}